import React, { useEffect, useMemo, useState } from 'react'

// @mui material components
import {
  Divider,
  CircularProgress,
} from '@mui/material'

// Icons for steps
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined'
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined'

// Material Dashboard components
import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'

// Services & utils
import CrudService from 'services/cruds-service'
import AuthService from 'services/auth-service'

// Toasts
import { toast } from 'react-toastify'
import 'material-react-toastify/dist/ReactToastify.css'

// Step components
import EntityStep from './steps/EntityStep'
import AddressesStep from './steps/AddressesStep'
import ContactsStep from './steps/ContactsStep'
import ItemsStep from './steps/ItemsStep'
import ProviderSharingStep from './steps/ProviderSharingStep'
import ReviewStep from './steps/ReviewStep'
import PostSetupStep from './steps/PostSetupStep'
import { useNavigate } from 'react-router-dom'
import { loadShareableProviders } from './onboardingApi'
import StepBar from '../components/StepBar'
import AiPrefillDialog from './ai/AiPrefillDialog'
import { mergeSelectedAiDraft } from './ai/aiPrefillAdapter'
import { Alert } from '@mui/material'
import useClaimCustomerInstall from 'pages/onboarding/claim/useClaimCustomerInstall'
import { applyNewCustomerDefaults, newCustomerDefaults } from 'pages/onboarding/claim/customerIdentity'

const LOCAL_STORAGE_KEY_STATE = 'customerOnboardingState'
const LOCAL_STORAGE_KEY_STEP = 'customerOnboardingStep'
const LOCAL_STORAGE_KEY_CREATED_ENTITY = 'customerOnboardingCreatedEntityId'

export const WIZARD_STEPS = [
  { key: 'entity', label: 'Customer', icon: PersonAddAlt1OutlinedIcon },
  { key: 'address', label: 'Address', icon: HomeOutlinedIcon },
  { key: 'contacts', label: 'Contacts', icon: GroupsOutlinedIcon },
  { key: 'items', label: 'Tanks / Assets', icon: Inventory2OutlinedIcon },
  { key: 'provider-sharing', label: 'Provider Sharing', icon: ShareOutlinedIcon },
  { key: 'review', label: 'Review', icon: RateReviewOutlinedIcon },
]

const AI_PREFILL_ENABLED = process.env.REACT_APP_CUSTOMER_ONBOARDING_AI_PREFILL === "true";

export const createEmptyEntity = () => ({
  entity_type_id: '',
  name: '',
  first_name: '',
  last_name: '',
  status_type_id: '',
  company_id: '',
})

export const createEmptyAddress = () => ({
  id: crypto.randomUUID(),
  address_type_id: '',
  name: '',
  street_address1: '',
  street_address2: '',
  city: '',
  state: '',
  postal_code: '',
  country: '',
  latitude: '',
  longitude: '',
  status_type_id: '',
})

export const createEmptyContact = () => ({
  id: crypto.randomUUID(),
  contact_first_name: '',
  contact_last_name: '',
  contact_type_id: '',
  contact_status_type_id: '',
  email: '',
  phone: '',
  title: '',
  createUserAccount: false,
})

export const createEmptyItem = () => ({
  id: crypto.randomUUID(),
  name: '',
  map_identifier: '',
  item_id: '',
  address_index: 0,
  icon_id: '',
  status_type_id: '',
  is_default: false,
  attributes: {}, // keyed by attribute_id, e.g. { 31: '10', 32: '5' }
  provider_ids: [],
})

const createInitialState = () => ({
  entity: createEmptyEntity(),
  addresses: [createEmptyAddress()],
  contacts: [createEmptyContact()],
  items: [],
  serviceItems: [],
})

// Simple hook to manage AI Extract modal
// REMOVED: useAiExtractModal placeholder hook

function validateEntityStep(entity, dropdowns) {
  const errors = {}

  if (!entity.entity_type_id) {
    errors.entity_type_id = 'Entity type is required'
  }

  const entityType = dropdowns.entityTypes.find(
    (t) => String(t.entity_type_id) === String(entity.entity_type_id),
  )
  const isIndividual =
    entityType && typeof entityType.type_name === 'string'
      ? entityType.type_name.toLowerCase().includes('individual')
      : false

  if (isIndividual) {
    if (!entity.first_name) {
      errors.first_name = 'First name is required for Individual'
    }
    if (!entity.last_name) {
      errors.last_name = 'Last name is required for Individual'
    }
  } else if (!entity.name) {
    errors.name = 'Company name is required'
  }

  if (!entity.status_type_id) {
    errors.status_type_id = 'Status is required'
  }

  if (!entity.company_id) {
    errors.company_id = 'Company is required'
  }

  return { isValid: Object.keys(errors).length === 0, errors }
}

function validateAddressesStep(addresses) {
  const addressErrors = addresses.map((addr) => {
    const e = {}
    if (!addr.name) e.name = 'Name is required'
    if (!addr.street_address1) e.street_address1 = 'Street address is required'
    if (!addr.city) e.city = 'City is required'
    if (!addr.state) e.state = 'State is required'
    if (!addr.postal_code) e.postal_code = 'Postal code is required'
    if (!addr.address_type_id) e.address_type_id = 'Address type is required'
    if (!addr.country) e.country = 'Country is required'
    return e
  })

  const isValid = addressErrors.every((e) => Object.keys(e).length === 0)
  return { isValid, errors: addressErrors }
}

function validateContactsStep(contacts) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const contactErrors = contacts.map((c) => {
    const e = {}
    if (!c.contact_first_name) e.contact_first_name = 'First name is required'
    if (!c.contact_last_name) e.contact_last_name = 'Last name is required'
    if (!c.email || !emailRegex.test(c.email)) e.email = 'Valid email is required'
    if (!c.contact_type_id) e.contact_type_id = 'Contact type is required'
    return e
  })

  const isValid = contactErrors.every((e) => Object.keys(e).length === 0)
  return { isValid, errors: contactErrors }
}

function validateProviderSharingStep(items, providers) {
  const allowedProviderIds = new Set(
    providers.filter((provider) => provider.can_share === true).map((provider) => Number(provider.provider_id)),
  )

  const invalidItem = items.find((item) => {
    if (!Array.isArray(item.provider_ids)) return true
    const providerIds = item.provider_ids.map(Number)
    return (
      new Set(providerIds).size !== providerIds.length ||
      providerIds.some((providerId) => !allowedProviderIds.has(providerId))
    )
  })

  return invalidItem
    ? { isValid: false, errors: { providerSharing: 'One or more provider selections are invalid.' } }
    : { isValid: true, errors: {} }
}

function validateStepIndex(stepIndex, state, dropdowns, { claimMode = false } = {}) {
  const stepKey = WIZARD_STEPS[stepIndex]?.key

  if (stepKey === 'entity') {
    const res = validateEntityStep(state.entity, dropdowns)
    return { isValid: res.isValid, errors: { entity: res.errors } }
  }

  if (stepKey === 'address') {
    const res = validateAddressesStep(state.addresses)
    return { isValid: res.isValid, errors: { addresses: res.errors } }
  }

  if (stepKey === 'contacts') {
    const res = validateContactsStep(state.contacts)
    return { isValid: res.isValid, errors: { contacts: res.errors } }
  }

  if (stepKey === 'items') {
    // A device claim must end with the asset the device is installed on.
    if (claimMode && !state.items.length) {
      return { isValid: false, errors: { items: 'Add the tank or asset this device is installed on.' } }
    }
    return { isValid: true, errors: {} }
  }

  if (stepKey === 'provider-sharing') {
    return validateProviderSharingStep(state.items, dropdowns.providers)
  }

  if (stepKey === 'review') {
    const eRes = validateEntityStep(state.entity, dropdowns)
    const aRes = validateAddressesStep(state.addresses)
    const cRes = validateContactsStep(state.contacts)

    const isValid = eRes.isValid && aRes.isValid && cRes.isValid
    return {
      isValid,
      errors: {
        entity: eRes.errors,
        addresses: aRes.errors,
        contacts: cRes.errors,
      },
    }
  }

  return { isValid: true, errors: {} }
}

// Claim stage reported to the outer claim page, which owns the browser title.
const CLAIM_STAGE_BY_STEP = {
  entity: 'customer',
  address: 'address',
  contacts: 'contacts',
  items: 'asset_device',
  'provider-sharing': 'providers',
  review: 'review',
}

function CustomerOnboardingWizard({ onComplete, onCancel, claimContext } = {}) {
  const claimMode = Boolean(claimContext?.session)
  // Claim drafts are kept per claim so they never mix with standalone onboarding.
  const storageSuffix = claimMode ? `:claim:${claimContext.claimCode}` : ''
  const KEY_STATE = `${LOCAL_STORAGE_KEY_STATE}${storageSuffix}`
  const KEY_STEP = `${LOCAL_STORAGE_KEY_STEP}${storageSuffix}`
  const KEY_CREATED_ENTITY = `${LOCAL_STORAGE_KEY_CREATED_ENTITY}${storageSuffix}`
  const [state, setState] = useState(createInitialState)
  const [activeStep, setActiveStep] = useState(0)
  const [createdEntityId, setCreatedEntityId] = useState(null)
  const [lookupsLoaded, setLookupsLoaded] = useState(false)

  const [dropdowns, setDropdowns] = useState({
    entityTypes: [],
    addressTypes: [],
    contactTypes: [],
    statusOptions: [],
    entityStatusOptions: [],
    addressStatusOptions: [],
    contactStatusOptions: [],
    itemStatusOptions: [],
    companies: [],
    items: [],
    icons: [],
    serviceItems: [],
    providers: [],
  })

  const [loadingDropdowns, setLoadingDropdowns] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [attemptedSteps, setAttemptedSteps] = useState({})
  const [validationErrors, setValidationErrors] = useState({})
  const [checkingRole, setCheckingRole] = useState(true)
  const navigate = useNavigate()

  // AI Prefill dialog state and handler
  const [aiPrefillOpen, setAiPrefillOpen] = useState(false)

  const handleApplyAiDraft = (draft, selection) => {
    setState((previous) => mergeSelectedAiDraft(previous, draft, selection))

    const firstStep = ["entity", "addresses", "contacts"].find((group) =>
      Object.values(selection?.[group] || {}).some(Boolean),
    )
    const stepKeyByGroup = { entity: "entity", addresses: "address", contacts: "contacts" }
    if (firstStep) {
      handleStepChange(WIZARD_STEPS.findIndex((step) => step.key === stepKeyByGroup[firstStep]))
    }
  }

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(KEY_STATE)
      const storedStep = window.localStorage.getItem(KEY_STEP)
      const storedEntityId = window.localStorage.getItem(KEY_CREATED_ENTITY)

      if (stored) {
        const parsed = JSON.parse(stored)
        setState({
          ...createInitialState(),
          ...parsed,
        })
      }

      if (storedStep) {
        const stepIndex = Number(storedStep)
        if (!Number.isNaN(stepIndex) && stepIndex >= 0 && stepIndex < WIZARD_STEPS.length) {
          setActiveStep(stepIndex)
        }
      }

      if (storedEntityId) {
        setCreatedEntityId(storedEntityId)
      }
    } catch (err) {
      console.error('Failed to restore onboarding wizard state', err)
    }
  }, [])

  useEffect(() => {
    let active = true

    const checkRole = async () => {
      try {
        const profile = await AuthService.getProfile()
        const roleId = Number(profile?.included?.[0]?.id)

        if (!active) return
        if (![1, 7].includes(roleId)) {
          toast.error('Provider access is required to onboard a customer.')
          navigate('/onboarding', { replace: true })
          return
        }

      } catch (error) {
        if (active) {
          toast.error('Unable to verify provider access.')
          navigate('/onboarding', { replace: true })
        }
      } finally {
        if (active) setCheckingRole(false)
      }
    }

    checkRole()
    return () => {
      active = false
    }
  }, [navigate])

  useEffect(() => {
    try {
      window.localStorage.setItem(KEY_STATE, JSON.stringify(state))
      window.localStorage.setItem(KEY_STEP, String(activeStep))

      if (createdEntityId) {
        window.localStorage.setItem(KEY_CREATED_ENTITY, String(createdEntityId))
      }
    } catch (err) {
      console.error('Failed to persist onboarding wizard state', err)
    }
  }, [state, activeStep, createdEntityId])

  useEffect(() => {
    let isMounted = true

    const loadDropdowns = async () => {
      setLoadingDropdowns(true)
      try {
        const [onboardingDropData, providers] = await Promise.all([
          CrudService.onboardingDropData(),
          loadShareableProviders(),
        ])
        if (!isMounted) return

        setDropdowns({
          entityTypes: onboardingDropData.entityTypes || [],
          addressTypes: onboardingDropData.addressTypes || [],
          contactTypes: onboardingDropData.contactTypes || [],
          statusOptions: onboardingDropData.statusTypes || [],
          entityStatusOptions: onboardingDropData.statusTypes || [],
          addressStatusOptions: [],
          contactStatusOptions: [],
          itemStatusOptions: [],
          companies: onboardingDropData.company || [],
          items: onboardingDropData.items || [],
          icons: onboardingDropData.icons || [],
          serviceItems: onboardingDropData.serviceItems || [],
          providers,
        })
      } catch (err) {
        console.error('Failed to load onboarding dropdowns', err)
        toast.error('Failed to load dropdown data. Please refresh and try again.', {
          position: 'top-right',
        })
      } finally {
        if (isMounted) {
          setLoadingDropdowns(false)
          setLookupsLoaded(true)
        }
      }
    }

    loadDropdowns()

    return () => {
      isMounted = false
    }
  }, [])

  const currentValidation = useMemo(
    () => validateStepIndex(activeStep, state, dropdowns, { claimMode }),
    [activeStep, state, dropdowns, claimMode],
  )

  const claim = useClaimCustomerInstall({ claimContext, state, dropdowns, lookupsLoaded })
  const claimDefaults = useMemo(
    () => (claimMode && lookupsLoaded ? newCustomerDefaults(dropdowns) : null),
    [claimMode, lookupsLoaded, dropdowns],
  )

  // Claim mode: write Active status and the Rivio company into the entity
  // itself (not just placeholder text) so review and payload match.
  useEffect(() => {
    if (!claimMode || !lookupsLoaded) return
    setState((previous) => {
      const entity = applyNewCustomerDefaults(previous.entity, dropdowns)
      if (
        entity.status_type_id === previous.entity.status_type_id &&
        entity.company_id === previous.entity.company_id
      ) {
        return previous
      }
      return { ...previous, entity }
    })
  }, [claimMode, lookupsLoaded, dropdowns])

  const stepKeyForClaim = WIZARD_STEPS[activeStep]?.key
  useEffect(() => {
    if (!claimMode || claim.stage) return
    claimContext.onStage?.(CLAIM_STAGE_BY_STEP[stepKeyForClaim] || 'customer')
  }, [claimMode, stepKeyForClaim, claim.stage])

  const handleStepChange = (nextStep) => {
    setActiveStep(nextStep)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleNext = async () => {
    const { isValid, errors } = validateStepIndex(activeStep, state, dropdowns, { claimMode })
    setAttemptedSteps((prev) => ({ ...prev, [activeStep]: true }))
    setValidationErrors((prev) => ({ ...prev, ...errors }))

    if (!isValid) {
      toast.error('Please fix validation errors before continuing.', {
        position: 'top-right',
      })
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    const isReviewStep = WIZARD_STEPS[activeStep]?.key === 'review'
    if (isReviewStep) {
      await handleSubmit()
    } else {
      const next = Math.min(activeStep + 1, WIZARD_STEPS.length - 1)
      handleStepChange(next)
    }
  }

  const handleBack = () => {
    if (activeStep === 0) return
    const prevStep = activeStep - 1
    handleStepChange(prevStep)
  }

  const handleSubmit = async () => {
    const reviewStepIndex = WIZARD_STEPS.findIndex((step) => step.key === 'review')
    const { isValid, errors } = validateStepIndex(reviewStepIndex, state, dropdowns, { claimMode })
    setAttemptedSteps((prev) => 
      WIZARD_STEPS.reduce((result, _, index) => ({ ...result, [index]: true }), {})
    )
    setValidationErrors((prev) => ({ ...prev, ...errors }))

    if (!isValid) {
      toast.error('Please fix validation errors before submitting.', {
        position: 'top-right',
      })
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    if (claimMode) {
      await handleClaimSubmit()
      return
    }

    setSubmitting(true)
    try {
      const payload = {
        data: {
          type: 'entities',
          attributes: {
            entity: state.entity,
            addresses: state.addresses,
            contacts: state.contacts,
            items: state.items.map((item) => ({
              ...item,
              provider_ids: [...new Set(item.provider_ids.map(Number))],
            })),
            serviceItems: state.serviceItems,
          },
        },
      }
      const response = await CrudService.onboardingCustomer(payload)
      window.localStorage.removeItem(LOCAL_STORAGE_KEY_STATE)
      window.localStorage.setItem(LOCAL_STORAGE_KEY_STEP, String(0))

      toast.success('Customer onboarding completed successfully.', {
        position: 'top-right',
      })

      if (onComplete) {
        try {
          onComplete(response);
        } catch (callbackError) {
          console.error('Customer saved; installation return failed', callbackError);
          toast.error('Customer saved, but returning to installation failed. Do not create it again.');
        }
        return;
      }
      navigate('/onboarding')
    } catch (err) {
      console.error('Failed to submit onboarding entity', err)
      toast.error('Failed to create customer. Please try again.', {
        position: 'top-right',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const buildClaimPayload = ({ stripProvidersFor }) => ({
    data: {
      type: 'entities',
      attributes: {
        entity: state.entity,
        addresses: state.addresses,
        contacts: state.contacts,
        // The assigned asset's provider shares run as recorded follow-ups after
        // installation, so their outcome is verified and retryable.
        items: state.items.map((item) => ({
          ...item,
          provider_ids:
            item.id === stripProvidersFor ? [] : [...new Set(item.provider_ids.map(Number))],
        })),
        serviceItems: state.serviceItems,
      },
    },
  })

  const handleClaimSubmit = async () => {
    if (claim.blockers.length) {
      toast.error(claim.blockers[0], { position: 'top-right' })
      return
    }
    setSubmitting(true)
    try {
      const result = await claim.submit({
        createCustomer: (payload) => CrudService.onboardingCustomer(payload),
        buildPayload: buildClaimPayload,
      })
      if (!result) return
      window.localStorage.removeItem(KEY_STATE)
      window.localStorage.removeItem(KEY_STEP)
      window.localStorage.removeItem(KEY_CREATED_ENTITY)
      claimContext.onInstalled?.(result)
    } catch (err) {
      console.error('Failed to save customer for device installation', err)
      toast.error(err?.message || 'Failed to create customer. Nothing was saved; please try again.', {
        position: 'top-right',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const isAttempted = attemptedSteps[activeStep]
  const disableNext =
    submitting || (isAttempted && activeStep < WIZARD_STEPS.length - 1 && !currentValidation.isValid)

  const stepKey = WIZARD_STEPS[activeStep]?.key
  return (
    <MDBox sx={{ mx: 'auto' }}>
      {stepKey !== 'done' && <StepBar current={stepKey} STEPS={WIZARD_STEPS} />}
      <Divider sx={{ my: 2 }} />

      {loadingDropdowns || checkingRole ? (
        <MDBox display="flex" justifyContent="center" alignItems="center" py={6}>
          <CircularProgress size={32} />
        </MDBox>
      ) : (
        <>
          {stepKey === 'entity' && (
            <EntityStep
              entity={state.entity}
              setEntity={(updater) =>
                setState((prev) => ({
                  ...prev,
                  entity: typeof updater === 'function' ? updater(prev.entity) : updater,
                }))
              }
              dropdowns={dropdowns}
              errors={validationErrors.entity || {}}
              onOpenAiModal={AI_PREFILL_ENABLED ? () => setAiPrefillOpen(true) : undefined}
            />
          )}

          {stepKey === 'address' && (
            <AddressesStep
              addresses={state.addresses}
              setAddresses={(updater) =>
                setState((prev) => ({
                  ...prev,
                  addresses: typeof updater === 'function' ? updater(prev.addresses) : updater,
                }))
              }
              dropdowns={dropdowns}
              errors={validationErrors.addresses || []}
            />
          )}

          {stepKey === 'contacts' && (
            <ContactsStep
              contacts={state.contacts}
              setContacts={(updater) =>
                setState((prev) => ({
                  ...prev,
                  contacts: typeof updater === 'function' ? updater(prev.contacts) : updater,
                }))
              }
              dropdowns={dropdowns}
              errors={validationErrors.contacts || []}
            />
          )}

          {stepKey === 'items' && (
            <ItemsStep
              items={state.items}
              setItems={(updater) =>
                setState((prev) => ({
                  ...prev,
                  items: typeof updater === 'function' ? updater(prev.items) : updater,
                }))
              }
              dropdowns={dropdowns}
            />
          )}

          {stepKey === 'provider-sharing' && (
            <ProviderSharingStep
              items={state.items}
              setItems={(updater) =>
                setState((previous) => ({
                  ...previous,
                  items: typeof updater === 'function' ? updater(previous.items) : updater,
                }))
              }
              providers={dropdowns.providers}
              loading={loadingDropdowns}
            />
          )}

          {stepKey === 'review' && (
            <ReviewStep
              state={state}
              dropdowns={dropdowns}
              lookupsLoaded={lookupsLoaded}
              createdEntityId={claim.saved?.entityId}
              onEditCustomer={claim.saved?.entityId ? undefined : () => handleStepChange(0)}
            />
          )}

          {claimMode && stepKey === 'review' && (
            <MDBox mt={3}>
              {claim.saved?.entityId && (
                <Alert severity={claim.assignmentError ? 'error' : 'info'} sx={{ mb: 2 }}>
                  {claim.assignmentError
                    ? `Customer saved (Customer #${claim.saved.entityId}); device assignment needs attention: ${claim.assignmentError}`
                    : `Customer saved as Customer #${claim.saved.entityId}. Confirming assigns the device without creating the customer again.`}
                </Alert>
              )}
              {claim.reviewSection}
            </MDBox>
          )}

          {claimMode && stepKey === 'entity' && claimDefaults?.errors?.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {claimDefaults.errors.join(' ')}
            </Alert>
          )}

          {claimMode && stepKey === 'items' && validationErrors.items && attemptedSteps[activeStep] && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {validationErrors.items}
            </Alert>
          )}

          {stepKey === 'post-setup' && <PostSetupStep createdEntityId={createdEntityId} />}

          {AI_PREFILL_ENABLED && (
            <AiPrefillDialog
              open={aiPrefillOpen}
              onClose={() => setAiPrefillOpen(false)}
              onApply={handleApplyAiDraft}
              currentState={state}
              dropdowns={dropdowns}
            />
          )}

          <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={4}>
            <MDButton variant="outlined" color="secondary" onClick={handleBack} disabled={activeStep === 0 || Boolean(claim.saved?.entityId)}>
              Back
            </MDButton>

            {onCancel && <MDButton variant="outlined" color="secondary"
              disabled={submitting || Boolean(claim.saved?.entityId)} onClick={onCancel}>
              Back to device installation
            </MDButton>}

            <MDBox display="flex" alignItems="center" gap={2}>
              {submitting && <CircularProgress size={20} />}
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleNext}
                disabled={disableNext || (claimMode && stepKey === 'review' && claim.blockers.length > 0)}
              >
                {stepKey === 'review'
                  ? claimMode
                    ? claim.assignmentError
                      ? 'Retry device assignment'
                      : 'Confirm & install device'
                    : 'Submit'
                  : 'Next'}
              </MDButton>
            </MDBox>
          </MDBox>
        </>
      )}
    </MDBox>
  )
}

export default CustomerOnboardingWizard

